import { useState, useEffect, useMemo } from 'react';
import { api } from '@/utils/api';
import { joinPersonName } from '@/utils/names';
import {
  FileText, Search, Filter, Eye, Send, CheckCircle, XCircle, Clock,
  Phone, Mail, MoreVertical, ChevronDown, RefreshCw, Receipt,
  TrendingUp, Users, Loader2,
} from 'lucide-react';

interface TrackingItem {
  id: string;
  number: string;
  title?: string;
  status: string;
  total: number | string;
  subtotal?: number | string;
  taxAmount?: number | string;
  contactName: string | null;
  contactPhone: string | null;
  createdAt: string;
  sentAt: string | null;
  createdByName: string | null;
  // Won-attribution (2026-07-15): the LEAD'S assigned rep — shown in preference
  // to createdByName so a manager who created/confirmed the record on a rep's
  // behalf is never credited as the rep.
  assignedRepName?: string | null;
}

interface TrackingStats {
  proposals: { draft: number; sent: number; viewed: number; accepted: number; rejected: number };
  salesOrders: { draft: number; sent: number; confirmed: number; cancelled: number; completed: number };
  conversionRate: number;
  avgResponseTime: string;
}

type StatusFilter = 'all' | 'draft' | 'sent' | 'viewed' | 'accepted' | 'rejected';
type TabType = 'proposals' | 'salesOrders';

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; icon: typeof Clock }> = {
  draft:    { label: 'Draft',    bg: 'bg-gray-100 dark:bg-gray-700',       text: 'text-gray-600 dark:text-gray-300',   icon: Clock },
  sent:     { label: 'Sent',     bg: 'bg-blue-50 dark:bg-blue-900/30',     text: 'text-blue-600 dark:text-blue-400',   icon: Send },
  viewed:   { label: 'Viewed',   bg: 'bg-amber-50 dark:bg-amber-900/30',   text: 'text-amber-600 dark:text-amber-400', icon: Eye },
  accepted: { label: 'Accepted', bg: 'bg-green-50 dark:bg-green-900/30',   text: 'text-green-600 dark:text-green-400', icon: CheckCircle },
  rejected: { label: 'Rejected', bg: 'bg-red-50 dark:bg-red-900/30',       text: 'text-red-600 dark:text-red-400',     icon: XCircle },
};

function formatINR(val: number | string) {
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n)) return '-';
  return n.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '-';
  try {
    return new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(dateStr));
  } catch { return '-'; }
}

function timeAgo(dateStr: string | null) {
  if (!dateStr) return '';
  try {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  } catch { return ''; }
}

export function ProposalTrackingPage() {
  const [tab, setTab] = useState<TabType>('proposals');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [proposals, setProposals] = useState<TrackingItem[]>([]);
  const [salesOrders, setSalesOrders] = useState<TrackingItem[]>([]);
  const [stats, setStats] = useState<TrackingStats | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pRes, soRes, sRes] = await Promise.all([
        api.get('/telecaller/tracking/proposals', { params: { limit: 200 } }),
        api.get('/sales-orders', { params: { limit: 200 } }),
        api.get('/telecaller/tracking/stats'),
      ]);
      setProposals(pRes.data?.data || []);
      setSalesOrders((soRes.data?.data || []).map((so: any) => ({
        id: so.id,
        number: so.number || so.orderNumber || '',
        title: so.title || '',
        status: so.status || 'draft',
        total: so.total || so.grandTotal || 0,
        contactName: so.contact?.firstName ? joinPersonName(so.contact.firstName, so.contact.lastName) : so.customerName || null,
        contactPhone: so.contact?.phone || null,
        createdAt: so.createdAt,
        sentAt: so.confirmedAt || null,
        createdByName: so.createdBy?.firstName ? `${so.createdBy.firstName} ${so.createdBy.lastName || ''}`.trim() : null,
        assignedRepName: so.assignedRep?.firstName ? `${so.assignedRep.firstName} ${so.assignedRep.lastName || ''}`.trim() : null,
      })));
      setStats(sRes.data?.data || null);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const items = tab === 'proposals' ? proposals : salesOrders;

  const filtered = useMemo(() => {
    let list = items;
    if (statusFilter !== 'all') {
      list = list.filter(i => i.status?.toLowerCase() === statusFilter);
    }
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(i =>
        (i.number || '').toLowerCase().includes(s) ||
        (i.contactName || '').toLowerCase().includes(s) ||
        (i.title || '').toLowerCase().includes(s)
      );
    }
    return list;
  }, [items, statusFilter, search]);

  const tabStats = tab === 'proposals' ? stats?.proposals : stats?.salesOrders;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              Offers
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Track proposals, sales orders & deal conversions
            </p>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Stats cards */}
        {stats && (
          <div className="grid grid-cols-4 gap-3 mt-4">
            <_StatCard
              label="Conversion Rate"
              value={`${stats.conversionRate}%`}
              icon={TrendingUp}
              color="text-green-600"
              bg="bg-green-50 dark:bg-green-900/20"
            />
            <_StatCard
              label="Avg Response"
              value={stats.avgResponseTime}
              icon={Clock}
              color="text-blue-600"
              bg="bg-blue-50 dark:bg-blue-900/20"
            />
            <_StatCard
              label="Total Sent"
              value={String((stats.proposals.sent + stats.proposals.viewed + stats.proposals.accepted + stats.proposals.rejected) + (stats.salesOrders?.sent || 0) + (stats.salesOrders?.confirmed || 0) + (stats.salesOrders?.cancelled || 0) + (stats.salesOrders?.completed || 0))}
              icon={Send}
              color="text-purple-600"
              bg="bg-purple-50 dark:bg-purple-900/20"
            />
            <_StatCard
              label="Won"
              value={String(stats.proposals.accepted + (stats.salesOrders?.confirmed || 0))}
              icon={CheckCircle}
              color="text-teal-600"
              bg="bg-teal-50 dark:bg-teal-900/20"
            />
          </div>
        )}
      </div>

      {/* Tabs + Filter bar */}
      <div className="px-6 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 p-0.5 rounded-lg">
            {(['proposals', 'salesOrders'] as TabType[]).map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition ${
                  tab === t
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {t === 'proposals' ? `Proposals (${proposals.length})` : `Sales Orders (${salesOrders.length})`}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8 pr-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg w-52 focus:outline-none focus:ring-1 focus:ring-teal-500 text-gray-900 dark:text-white placeholder-gray-400"
              />
            </div>

            {/* Status filter chips */}
            <div className="flex gap-1">
              {(['all', 'sent', 'viewed', 'accepted', 'rejected'] as StatusFilter[]).map(s => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition ${
                    statusFilter === s
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {s === 'all' ? 'All' : STATUS_CONFIG[s]?.label || s}
                  {s !== 'all' && tabStats ? ` (${(tabStats as any)[s] || 0})` : ''}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-teal-600 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <FileText className="w-12 h-12 mb-3 opacity-40" />
            <p className="text-base font-medium">No {tab === 'proposals' ? 'proposals' : 'sales orders'} found</p>
            <p className="text-sm mt-1">
              {statusFilter !== 'all' ? `No ${statusFilter} ${tab === 'proposals' ? 'proposals' : 'sales orders'} to show` : `${tab === 'proposals' ? 'Proposals' : 'Sales Orders'} will appear here when created`}
            </p>
          </div>
        ) : (
          <div className="grid gap-3">
            {filtered.map(item => (
              <_TrackingCard key={item.id} item={item} isQuote={tab === 'salesOrders'} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function _StatCard({ label, value, icon: Icon, color, bg }: {
  label: string; value: string; icon: typeof Clock; color: string; bg: string;
}) {
  return (
    <div className={`${bg} rounded-xl px-4 py-3 flex items-center gap-3`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${bg}`}>
        <Icon className={`w-4.5 h-4.5 ${color}`} />
      </div>
      <div>
        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">{label}</p>
        <p className={`text-lg font-bold ${color}`}>{value}</p>
      </div>
    </div>
  );
}

function _TrackingCard({ item, isQuote }: { item: TrackingItem; isQuote: boolean }) {
  const status = (item.status || 'draft').toLowerCase();
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const StatusIcon = cfg.icon;
  const amount = typeof item.total === 'number' ? item.total : parseFloat(item.total as string);

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:shadow-md transition group">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-bold text-gray-900 dark:text-white">
              {item.contactName || 'Unknown Contact'}
            </span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase rounded ${cfg.bg} ${cfg.text}`}>
              <StatusIcon className="w-3 h-3" />
              {cfg.label}
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
            {item.number}
            {item.title ? ` — ${item.title}` : ''}
          </p>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
            {item.sentAt ? (
              <span className="flex items-center gap-1">
                <Send className="w-3 h-3" />
                Sent {timeAgo(item.sentAt)}
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Created {formatDate(item.createdAt)}
              </span>
            )}
            {/* Won-attribution (2026-07-15): show the LEAD'S assigned rep only.
                Never fall back to createdByName — a record with no linked lead
                has no rep to credit, and the creator (often a manager/owner like
                Karan) must not be shown as the winning rep. */}
            {item.assignedRepName && (
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {item.assignedRepName}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 pl-4">
          {!isNaN(amount) && amount > 0 && (
            <span className="text-base font-bold text-gray-900 dark:text-white">
              {formatINR(amount)}
            </span>
          )}
          <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition">
            {item.contactPhone && (
              <a href={`tel:${item.contactPhone}`} className="w-7 h-7 flex items-center justify-center rounded-lg bg-teal-50 dark:bg-teal-900/30 text-teal-600 hover:bg-teal-100">
                <Phone className="w-3.5 h-3.5" />
              </a>
            )}
            {item.contactPhone && (
              <a href={`mailto:${item.contactPhone}`} className="w-7 h-7 flex items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 hover:bg-blue-100">
                <Mail className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
